/*
	ReactTemplates compatibility shim (WEB-7832).

	window.ReactTemplates is a global aggregator of shared React components/helpers, historically
	created and populated from inside the Angular tree. Its registrars now all live outside it, in
	ui/app/babel/components/LegacyReactTemplates/ (WEB-9930), still pulled into the bundle by
	ui/app/babel/legacy-react.js. ~77 of its keys are read by code outside the Angular tree, so the
	global must survive the Phase 6 deletion of ui/app/upsales/.

	This module owns the global's creation, mirroring the ToolsShim strategy:
	1. installReactTemplatesShim() runs at the React entry, before legacy-react.js loads the
	   Angular tree. It creates the global and its namespaces.
	2. Every writer now lives outside the tree and merges its keys into the existing global
	   instead of replacing it; what remains inside the tree is only Angular directives reading
	   the globals to mount them.
	3. As components move out of the Angular tree (batch by batch, one namespace at a time),
	   this module imports and registers them.
	4. When every key read outside the tree is registered from here, the app no longer depends
	   on the Angular tree for the global.

	Calling install() more than once, or after the Angular tree has already populated the global
	(as in the jest smoketest harness), is safe: existing namespaces and keys are kept.
*/
// The legacy aggregator (WEB-8019): self-creates the global's namespaces and registers ~20
// TOOLS.* helpers + ROW.formRow at module scope. Evaluating it here (at the React entry) is
// safe — its module scope reads no Tools/window state — and earlier than the old
// legacy-react.js position, which only tree files relied on. It has to stay a side-effect import:
// everything below assumes the global and its namespaces already exist.
// WEB-9232 retired the TOOLS namespace, so install() no longer re-assigns any TOOLS key — every
// consumer imports the helper it needs directly. ui/app/upsales/** still reads ~16 of them off the
// global, and the aggregator's own module-eval registrations above cover those.
import 'App/babel/components/LegacyReactTemplates';
import FormRow from 'App/babel/components/LegacyReactTemplates/listForms';
// The legacy upFilters engine (WEB-8020): upListFilters + filters/*.jsx self-register onto
// window.ReactTemplates.upFilters at module eval. Side-effect import runs AFTER the aggregator
// import above (which creates window.ReactTemplates), so the namespace exists when it evals.
import 'App/babel/components/LegacyFilters';
// upToggle (WEB-8859): registers ReactTemplates.checkbox AND assigns into
// ReactTemplates.upFilters.components.toggle at module scope. That assignment reads the
// upFilters.components object LegacyFilters creates, so this import MUST stay after it —
// reversed, the assignment throws on undefined.
import 'App/babel/components/LegacyReactTemplates/upToggle';
// The last externally-read leaves (WEB-8861). upCurrencies and multiInputRange dropped out here in
// WEB-9574: their only consumers import them directly now, so nothing needs the shim to evaluate
// them.
import 'App/babel/components/LegacyReactTemplates/upStepInt';
import 'App/babel/components/LegacyReactTemplates/esignAppPlaceholder';
// appPreview registers ReactTemplates.appPreview -- a top-level key, despite living in the admin
// subtree -- which pages/integrations.jsx and esignAppPlaceholder.jsx read off the global. It was
// reached via the LegacyAdmin barrel until WEB-9890 deleted that; imported here directly so it keeps
// loading. Goes with the key in WEB-9205.
import 'App/babel/components/LegacyAdmin/components/appPreview';
import 'App/babel/components/LegacyReactTemplates/processedBy';
// The row registrations (WEB-8988). These assign into ReactTemplates.ROW / ROWSTATICS, which the
// LegacyReactTemplates aggregator creates at module eval above — so the namespaces already exist.
import 'App/babel/components/LegacyReactTemplates/listContacts';
import 'App/babel/components/LegacyReactTemplates/listVisitors';

// Capture the populated namespaces so a re-install after global replacement (the
// mixed-jest-harness case) restores them — the side-effect imports only run once.
const legacyGlobal = (
	window as unknown as {
		ReactTemplates?: {
			upFilters?: unknown;
			checkbox?: unknown;
			upStepInt?: unknown;
			esignAppPlaceholder?: unknown;
			processedByColumn?: unknown;
			ROW?: Record<string, unknown>;
			ROWSTATICS?: Record<string, unknown>;
		};
	}
).ReactTemplates;
const legacyUpFilters = legacyGlobal?.upFilters;
const legacyCheckbox = legacyGlobal?.checkbox;
const legacyUpStepInt = legacyGlobal?.upStepInt;
const legacyEsignAppPlaceholder = legacyGlobal?.esignAppPlaceholder;
const legacyProcessedByColumn = legacyGlobal?.processedByColumn;
// install() sets ROW.formRow explicitly but not these, so they need capturing individually
// (WEB-8988).
const legacyRowKeys: [string, unknown][] = [
	['contactRow', legacyGlobal?.ROW?.contactRow],
	['SegmentInfoRow', legacyGlobal?.ROW?.SegmentInfoRow],
	['visitorRow', legacyGlobal?.ROW?.visitorRow]
];
const legacyRowStaticsKeys: [string, unknown][] = [
	['contactRow', legacyGlobal?.ROWSTATICS?.contactRow],
	['visitorRow', legacyGlobal?.ROWSTATICS?.visitorRow]
];

// TOOLS and INPUTS are still listed even though WEB-9232 and WEB-9574 removed every key
// registration for both. ui/app/upsales/** still reads ~16 TOOLS keys off the global, and reads
// INPUTS.upCountry (upCountrySelect.js) -- while its writer, LegacyReactTemplates/upCountry.jsx,
// assigns into the namespace, so INPUTS has to exist. Creating them here (as well as in the
// aggregator's module eval) keeps a re-install after something replaced the global from leaving
// the tree with no namespace. Both go when Phase 6 deletes the tree.
const NAMESPACES = ['TOOLS', 'ROW', 'ROWSTATICS', 'OTHER', 'INPUTS', 'MODALS', 'navbar'] as const;

const installReactTemplatesShim = () => {
	// The full ReactTemplates type describes the populated end state; population is incremental
	// by design, so the skeleton is built through an untyped alias.
	const w = window as unknown as { ReactTemplates: Record<string, Record<string, unknown>> };
	w.ReactTemplates = w.ReactTemplates || {};
	for (const ns of NAMESPACES) {
		w.ReactTemplates[ns] = w.ReactTemplates[ns] || {};
	}
	w.ReactTemplates.navbar.searchRows = w.ReactTemplates.navbar.searchRows || {};

	// upFilters (WEB-8020): restore the captured, fully-populated namespace (filterMenu +
	// components + filterTypes) — survives Phase 6 and re-installs after global replacement.
	if (legacyUpFilters) {
		w.ReactTemplates.upFilters = legacyUpFilters as Record<string, unknown>;
	}
	// checkbox (WEB-8859): likewise a single component. Its sibling key,
	// upFilters.components.toggle, rides along with the restored upFilters namespace above.
	if (legacyCheckbox) {
		(w.ReactTemplates as Record<string, unknown>).checkbox = legacyCheckbox;
	}
	// The last leaves (WEB-8861), all single components restored as plain keys.
	const leaves: [string, unknown][] = [
		['upStepInt', legacyUpStepInt],
		['esignAppPlaceholder', legacyEsignAppPlaceholder],
		['processedByColumn', legacyProcessedByColumn]
	];
	for (const [key, value] of leaves) {
		if (value) {
			(w.ReactTemplates as Record<string, unknown>)[key] = value;
		}
	}
	for (const [key, value] of legacyRowKeys) {
		if (value) {
			w.ReactTemplates.ROW[key] = value;
		}
	}
	for (const [key, value] of legacyRowStaticsKeys) {
		if (value) {
			w.ReactTemplates.ROWSTATICS[key] = value;
		}
	}

	w.ReactTemplates.ROW.formRow = FormRow;
};

export default installReactTemplatesShim;
